import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { stanjeRobeCustomersAPI, stanjeRobeProductsAPI, stanjeRobeStockOutAPI } from '../services/api';
import { StanjeRobeCustomer, StanjeRobeProduct, StanjeRobeStockOutRecord } from '../types';
import { showCustomAlert } from './CustomAlert';
import { computeM2FromQuantity, formatM2, formatStanjeRobeDate } from '../utils/stanjeRobeCalc';
import { groupStockOutShipments, StanjeRobeShipmentGroup } from '../utils/stanjeRobeShipments';
import { customerHasBrand, getCustomerBrandIds } from '../utils/stanjeRobeCustomerBrands';

type ShipmentLine = {
  productId: string;
  productName: string;
  brandName: string;
  quantity: number;
  squareMeters: number;
};

const StanjeRobeStockOut: React.FC = () => {
  const [records, setRecords] = useState<StanjeRobeStockOutRecord[]>([]);
  const [products, setProducts] = useState<StanjeRobeProduct[]>([]);
  const [customers, setCustomers] = useState<StanjeRobeCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createVisible, setCreateVisible] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<StanjeRobeShipmentGroup | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [outDate, setOutDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ShipmentLine[]>([]);
  const [lineDraft, setLineDraft] = useState({ productId: '', quantity: 1 });

  const shipments = useMemo(() => groupStockOutShipments(records), [records]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c._id === customerId),
    [customers, customerId]
  );

  const customerBrandIds = useMemo(() => {
    if (!selectedCustomer) return new Set<string>();
    return new Set(getCustomerBrandIds(selectedCustomer));
  }, [selectedCustomer]);

  const availableProducts = useMemo(() => {
    if (!customerBrandIds.size) return products;
    return products.filter((p) => {
      const bid = typeof p.brandId === 'string' ? p.brandId : p.brandId?._id;
      return bid ? customerBrandIds.has(bid) : false;
    });
  }, [products, customerBrandIds]);

  const productOptions = useMemo(
    () => availableProducts.map((p) => ({ label: `${p.brandName} – ${p.name}`, value: p._id })),
    [availableProducts]
  );

  const customerOptions = useMemo(
    () => customers.map((c) => ({ label: c.name, value: c._id })),
    [customers]
  );

  const draftM2 = useMemo(() => {
    const product = availableProducts.find((p) => p._id === lineDraft.productId);
    return computeM2FromQuantity(lineDraft.quantity, 'paleta', product);
  }, [lineDraft, availableProducts]);

  const totalBatchM2 = useMemo(
    () => lines.reduce((sum, l) => sum + l.squareMeters, 0),
    [lines]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, r] = await Promise.all([
        stanjeRobeProductsAPI.getAll(),
        stanjeRobeCustomersAPI.getAll(),
        stanjeRobeStockOutAPI.getAll()
      ]);
      setProducts(p);
      setCustomers(c);
      setRecords(r);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setCustomerId('');
    setOutDate(new Date());
    setNotes('');
    setLines([]);
    setLineDraft({ productId: '', quantity: 1 });
    setError('');
    setCreateVisible(true);
  };

  const openView = (group: StanjeRobeShipmentGroup) => {
    setSelectedShipment(group);
    setViewVisible(true);
  };

  const handleCustomerChange = (value: string) => {
    setCustomerId(value);
    if (!value) return;
    const customer = customers.find((c) => c._id === value);
    if (!customer) return;
    setLines((prev) =>
      prev.filter((line) => {
        const product = products.find((p) => p._id === line.productId);
        const pb = product ? (typeof product.brandId === 'string' ? product.brandId : product.brandId?._id) : null;
        return customerHasBrand(customer, pb);
      })
    );
    if (lineDraft.productId) {
      const product = products.find((p) => p._id === lineDraft.productId);
      const pb = product ? (typeof product.brandId === 'string' ? product.brandId : product.brandId?._id) : null;
      if (!customerHasBrand(customer, pb)) setLineDraft({ productId: '', quantity: 1 });
    }
  };

  const addLine = () => {
    if (!lineDraft.productId) {
      showCustomAlert.warning('Odaberite robu');
      return;
    }
    if (!lineDraft.quantity || lineDraft.quantity <= 0) {
      showCustomAlert.warning('Količina mora biti veća od 0');
      return;
    }
    if (lines.some((l) => l.productId === lineDraft.productId)) {
      showCustomAlert.warning('Ova roba je već dodata');
      return;
    }
    const product = availableProducts.find((p) => p._id === lineDraft.productId);
    if (!product) return;
    const squareMeters = computeM2FromQuantity(lineDraft.quantity, 'paleta', product);
    setLines((prev) => [
      ...prev,
      {
        productId: product._id,
        productName: product.name,
        brandName: product.brandName,
        quantity: lineDraft.quantity,
        squareMeters
      }
    ]);
    setLineDraft({ productId: '', quantity: 1 });
  };

  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!lines.length) {
      setError('Dodajte bar jednu stavku');
      return;
    }
    try {
      setError('');
      await stanjeRobeStockOutAPI.createBatch({
        customerId: customerId || undefined,
        outDate: outDate.toISOString(),
        notes,
        items: lines.map((l) => ({ productId: l.productId, quantity: l.quantity }))
      });
      setCreateVisible(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri čuvanju isporuke');
    }
  };

  const handleDeleteShipment = (group: StanjeRobeShipmentGroup) => {
    showCustomAlert.confirm('Obrisati ovu isporuku?', async () => {
      try {
        if (group.shipmentId) {
          await stanjeRobeStockOutAPI.deleteShipment(group.shipmentId);
        } else {
          await stanjeRobeStockOutAPI.delete(group.items[0]._id);
        }
        if (selectedShipment?.key === group.key) setViewVisible(false);
        await load();
      } catch (err: any) {
        showCustomAlert.error(err?.response?.data?.message || 'Greška pri brisanju');
      }
    });
  };

  const actionsBody = (group: StanjeRobeShipmentGroup) => (
    <div className="flex gap-2">
      <Button icon="pi pi-eye" className="p-button-sm" onClick={() => openView(group)} />
      <Button icon="pi pi-trash" className="p-button-sm" severity="danger" onClick={() => handleDeleteShipment(group)} />
    </div>
  );

  const createFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setCreateVisible(false)} />
      <Button label="Sačuvaj isporuku" icon="pi pi-check" severity="info" onClick={handleSubmit} loading={loading} />
    </div>
  );

  const viewFooter = (
    <div className="flex justify-content-end gap-2">
      {selectedShipment && (
        <Button
          label="Obriši"
          icon="pi pi-trash"
          severity="danger"
          className="p-button-outlined"
          onClick={() => handleDeleteShipment(selectedShipment)}
        />
      )}
      <Button label="Zatvori" className="p-button-secondary p-button-outlined" onClick={() => setViewVisible(false)} />
    </div>
  );

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Skidanje sa stanja</h2>
          <div className="text-600 mt-1">Isporuke robe kupcima (u paletama)</div>
        </div>
        <Button label="Nova isporuka" icon="pi pi-plus" severity="info" onClick={openCreate} disabled={!products.length} />
      </div>

      {error && !createVisible && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable
          value={shipments}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          emptyMessage="Nema isporuka"
          className="p-datatable-sm"
          sortField="outDate"
          sortOrder={-1}
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column header="Datum" body={(g: StanjeRobeShipmentGroup) => formatStanjeRobeDate(g.outDate)} sortable sortField="outDate" style={{ width: '130px' }} />
          <Column field="customerName" header="Kupac" />
          <Column header="Broj stavki" body={(g: StanjeRobeShipmentGroup) => g.items.length} style={{ width: '120px' }} />
          <Column header="Ukupno m²" body={(g: StanjeRobeShipmentGroup) => formatM2(g.totalM2)} style={{ width: '120px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '120px' }} />
        </DataTable>
      </Card>

      <Dialog
        header="Nova isporuka"
        visible={createVisible}
        onHide={() => setCreateVisible(false)}
        style={{ width: 'min(920px, 96vw)' }}
        footer={createFooter}
        modal
        draggable={false}
      >
        <div className="p-fluid">
          {error && createVisible && <Message severity="error" text={error} className="mb-3" />}

          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Kupac (opciono)</label>
                <Dropdown
                  value={customerId}
                  options={customerOptions}
                  onChange={(e) => handleCustomerChange(e.value || '')}
                  showClear
                  placeholder="Odaberite kupca"
                  filter
                  appendTo="self"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="field">
                <label>Datum isporuke</label>
                <Calendar value={outDate} onChange={(e) => setOutDate((e.value as Date) || new Date())} dateFormat="dd/mm/yy" showIcon appendTo="self" className="w-full" />
              </div>
            </div>
          </div>

          <h3 className="m-0 mb-3">Dodaj stavke</h3>
          <div className="grid">
            <div className="col-12 md:col-7">
              <div className="field">
                <label>Roba *</label>
                <Dropdown
                  value={lineDraft.productId}
                  options={productOptions}
                  onChange={(e) => setLineDraft((p) => ({ ...p, productId: e.value || '' }))}
                  filter
                  placeholder="Odaberite robu"
                  appendTo="self"
                  className="w-full"
                />
              </div>
            </div>
            <div className="col-12 md:col-3">
              <div className="field">
                <label>Paleta *</label>
                <InputNumber value={lineDraft.quantity} onValueChange={(e) => setLineDraft((p) => ({ ...p, quantity: e.value ?? 0 }))} min={0} maxFractionDigits={3} />
              </div>
            </div>
            <div className="col-12 md:col-2">
              <div className="field">
                <label aria-hidden="true" className="block" style={{ visibility: 'hidden' }}>.</label>
                <Button label="Dodaj" icon="pi pi-plus" onClick={addLine} className="w-full" />
              </div>
            </div>
            {lineDraft.productId && draftM2 > 0 && (
              <div className="col-12 text-600 text-sm mb-2">
                ≈ {formatM2(draftM2)} m²
              </div>
            )}
          </div>

          <h3 className="m-0 mt-2 mb-2">Dodate stavke</h3>
          <DataTable value={lines} emptyMessage="Nema dodatih stavki" className="p-datatable-sm" responsiveLayout="stack" breakpoint="768px">
            <Column header="R.br" body={(_: ShipmentLine, opts: { rowIndex: number }) => opts.rowIndex + 1} style={{ width: '70px' }} />
            <Column field="brandName" header="Brend" style={{ width: '140px' }} />
            <Column field="productName" header="Roba" />
            <Column header="Paleta" body={(r: ShipmentLine) => r.quantity} style={{ width: '100px' }} />
            <Column header="m²" body={(r: ShipmentLine) => formatM2(r.squareMeters)} style={{ width: '110px' }} />
            <Column
              header="Akcije"
              body={(_: ShipmentLine, opts: { rowIndex: number }) => (
                <Button icon="pi pi-trash" severity="danger" className="p-button-sm" onClick={() => removeLine(opts.rowIndex)} />
              )}
              style={{ width: '100px' }}
            />
          </DataTable>

          {lines.length > 0 && (
            <div className="text-600 mt-2">
              Ukupno isporuke: <strong>{formatM2(totalBatchM2)} m²</strong>
            </div>
          )}

          <div className="field mt-3">
            <label>Napomena</label>
            <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Unesite napomenu" />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={selectedShipment ? `Isporuka – ${formatStanjeRobeDate(selectedShipment.outDate)}` : 'Isporuka'}
        visible={viewVisible}
        onHide={() => setViewVisible(false)}
        style={{ width: 'min(720px, 96vw)' }}
        footer={viewFooter}
        modal
        draggable={false}
      >
        {!selectedShipment ? (
          <div>—</div>
        ) : (
          <div className="p-fluid">
            <div className="grid mb-3">
              <div className="col-12 md:col-4">
                <div className="text-600">Datum</div>
                <div style={{ fontWeight: 700 }}>{formatStanjeRobeDate(selectedShipment.outDate)}</div>
              </div>
              <div className="col-12 md:col-4">
                <div className="text-600">Kupac</div>
                <div style={{ fontWeight: 700 }}>{selectedShipment.customerName}</div>
              </div>
              <div className="col-12 md:col-4">
                <div className="text-600">Ukupno m²</div>
                <div style={{ fontWeight: 700 }}>{formatM2(selectedShipment.totalM2)}</div>
              </div>
            </div>

            {selectedShipment.notes && (
              <div className="mb-3">
                <div className="text-600">Napomena</div>
                <div>{selectedShipment.notes}</div>
              </div>
            )}

            <h3 className="m-0 mb-2">Stavke</h3>
            <DataTable value={selectedShipment.items} className="p-datatable-sm" emptyMessage="Nema stavki" responsiveLayout="stack" breakpoint="768px">
              <Column field="brandName" header="Brend" style={{ width: '140px' }} />
              <Column field="productName" header="Roba" />
              <Column header="Paleta" body={(r: StanjeRobeStockOutRecord) => r.quantity} style={{ width: '100px' }} />
              <Column header="m²" body={(r: StanjeRobeStockOutRecord) => formatM2(r.squareMeters ?? 0)} style={{ width: '110px' }} />
            </DataTable>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default StanjeRobeStockOut;
